import classNames from 'classnames';
import debounce from 'debounce-promise';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { isBrowser, isMobile, isMobileOnly } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import ReactTable, { SortingRule, Instance, DerivedDataObject, Resize } from 'react-table-6';
import { HasClassName } from '../component-utils';
import { useSelector } from '../hook-utils';
import { Access, ContactBasic, ContactType, EntityType, ExtensionStatus, Identifier } from '../model';
import { actions } from '../store';
import { selectFavContactFolderId, selectMyAccess, selectMyData, selectMyself, selectRegions } from '../store/reducers/config';
import { getContactPages, getSelectedContact, isFavouriteContact, selectContacts } from '../store/reducers/contacts';
import { DirectoryView, selectDirectoryView, getGridSortOrder, selectGridColumnWidths, ColumnWidths } from '../store/reducers/localSettings';
import { selectExtension } from '../store/reducers/queueAgents';
import { TableColumn, TableRow } from '../table-utils';
import { useTeams } from '../TeamsProvider';
import { ContactForm } from './AgentView/ContactPage';
import './AgentView/ContactPage.scss';
import './ContactsPage.scss';
import { ReactComponent as AddContact } from './icons/add-people.svg';
import { ReactComponent as NewIcon } from './icons/contacts/new_contact.svg';
import { ReactComponent as DeleteIcon } from './icons/delete.svg';
import { ReactComponent as DialHomeIcon } from './icons/dial-home.svg';
import { ReactComponent as DialMobileIcon } from './icons/dial-mobile.svg';
import { ReactComponent as DialIcon } from './icons/dial-desk.svg';
import { ReactComponent as EmailIcon } from './icons/email.svg';
import { ReactComponent as ExtesnionPropsIcon } from './icons/extension-props.svg';
import { ReactComponent as FavRemoveIcon } from './icons/favourite-remove.svg';
import { ReactComponent as FavIcon } from './icons/favourite.svg';
import { ReactComponent as InstantMessagerIcon } from './icons/instantmessager/chat.svg';
import { ReactComponent as SearchIcon } from './icons/toolbar-search.svg';
import Input from './Input';
import { Menu, MenuDivider, MenuItem, MenuTarget, targetFromEvent } from './Menu';
import MobileContactsTable from './MobileView/ContactPage';
import MoreButton from './MoreButton';
import ProfilePhoto from './ProfilePhoto';

export interface ContactMenuState {
  target: MenuTarget
  item: ContactBasic
}

export default function ContactsPage() {
  console.log('Render ContactsPage')

  const dispatch = useDispatch()
  const view = useSelector(selectDirectoryView)
  const mySelf = useSelector(selectMyself)
  const myData = useSelector(selectMyData)
  const regions = useSelector(selectRegions)
  const folderId = useSelector(selectFavContactFolderId)
  const access = useSelector(selectMyAccess)
  const [filter, setFilter] = useState('')
  const [filterKeyPress, setFilterKeyPress] = useState('')

  if (!mySelf || !myData || !regions || !view || !access || view.contactType === undefined) return null

  const table = isBrowser ? <ContactsTable view={view} filter={filterKeyPress} /> : <MobileContactsTable view={view} filter={filter} />

  return <div className={classNames('ContactTablePage', isMobile && 'ContactMobileTablePage')}>
    <div className='Header'>
      <ContactsPageHeader view={view} />
      <div className='searchBox'>
        <Input placeholder='Description / phone number / email address...' className='search' value={filter} onChange={e => {
          setFilter(e.currentTarget.value)
        }} autocomplete={false} onKeyUp={e => {
          if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
            e.stopPropagation()
            e.preventDefault()

            setFilterKeyPress(filter)
            dispatch(actions.setSelectedContact(undefined))

          } else if (e.nativeEvent.code === 'Escape') {
            setFilter('')
            setFilterKeyPress('')
          }

        }} />
        <SearchIcon className='searchIcon' />
      </div>
      {!isMobile && view.regionId !== folderId && ((view.contactType === ContactType.Personal && access.features.directoryPersonalContactAdd) || access.features.directoryCompanyContactAdd) && <div title='Create new contact' className='add icon' onClick={() => dispatch(actions.setSelectedContact({ id: '', server: '' }))}>
        <AddContact />
      </div>}
    </div>
    {table}
  </div>
}

function ContactsPageHeader({ view }: { view: DirectoryView }) {
  const { inTeams } = useTeams()
  const myData = useSelector(selectMyData)
  const folderId = useSelector(selectFavContactFolderId)
  const regions = useSelector(selectRegions)

  let description = ''
  if (!myData || !folderId || !regions) return null

  if (view.regionId === myData.regionId) {
    if (view.contactType === ContactType.Personal) {
      description = 'Personal'
    }
  } else if (folderId === view.regionId) {
    description = 'Favourites'
  }

  if (description === '') {
    const r = regions.find(r => r.id === view.regionId)
    if (r) {
      description = r.name
    }
  }

  return <h1 className={classNames('GridPageHeader', !inTeams && 'standalone', isMobileOnly && 'GridPageHeaderMobile')}>
    {description}
  </h1>
}

interface Props extends HasClassName {
  view: DirectoryView
  filter: string
}

export function ContactsTable({ className, view, filter }: Props) {
  const [menuState, setMenuState] = useState<ContactMenuState>()
  const selected = useSelector(getSelectedContact)
  const dispatch = useDispatch()
  const contacts = useSelector(s => selectContacts(s))
  const data = useMemo(() => contacts ? contacts : [], [contacts])
  const access = useSelector(selectMyAccess)
  const [paging, setPaging] = useState({ page: 0, pageSize: 20 })
  const [curFilter, setCurFilter] = useState(filter)
  const updateFilter = useMemo(() => debounce(setCurFilter, 300), [])
  const pages = useSelector(getContactPages)
  const reactTable = useRef<ReactTable<ContactBasic>>(null);

  const myId = useSelector(selectMyself)
  const myself = useSelector(s => myId ? selectExtension(s, myId) : undefined)

  const gridType = 'contacts'
  const widths = useSelector(s => selectGridColumnWidths(s, gridType))
  const refColumnResizeState = useRef<ColumnWidths>()
  const s = useSelector(s => getGridSortOrder(s, gridType))
  const [sorted, setSorted] = useState<[SortingRule]>([s[0]])

  const onMore = useCallback((e: React.SyntheticEvent, item: ContactBasic, target: MenuTarget) => {
    e.preventDefault()
    e.stopPropagation()
    setMenuState({ target, item })
  }, [])

  useEffect(() => {
    updateFilter(filter)
  }, [filter, updateFilter])

  useEffect(() => {
    setPaging(x => ({ ...x, page: 0 }))
  }, [view])

  useEffect(() => {
    dispatch(actions.fetchContactsRequeust(view, {
      page: paging.page,
      pageSize: paging.pageSize,
      orderCol: sorted[0].id,
      orderDesc: sorted[0].desc || false,
      searchText: curFilter,
    }))
  }, [curFilter, dispatch, paging.page, paging.pageSize, sorted, view])

  useEffect(() => {
    return () => {
      //on unload
      dispatch(actions.setSelectedContact(undefined))
      dispatch(actions.clearSelectedNumber())
    }
  }, [dispatch])

  function onKeyDown(e: KeyboardEvent) {
    if (e.target instanceof HTMLElement) {
      const element = e.target as HTMLElement
      if (element.id.indexOf("mce") === 0) {
        return
      }
    }
    if (data == null || data.length === 0 || reactTable.current === null) return

    const table = reactTable.current as Instance
    const sorted: ContactBasic[] = (table.getSortedData(table.getResolvedState()).sortedData as DerivedDataObject[]).map(x => x._original)

    function selectContact(item: ContactBasic) {
      if (item.business) {
        dispatch(actions.setSelectedNumber(item.business))
      } else if (item.mobile) {
        dispatch(actions.setSelectedNumber(item.mobile))
      } else if (item.home) {
        dispatch(actions.setSelectedNumber(item.home))
      }
      dispatch(actions.setSelectedContact(item))
    }

    function selectFirstRow() {
      if (selected === null && sorted.length > -1) {
        selectContact(sorted[0])
        return true
      }
      return false
    }

    function selectNextUp() {
      const index = sorted.findIndex(d => selected !== null && selected.id.id === d.id)
      return index - 1
    }

    function selectNextDown() {
      const index = sorted.findIndex(d => selected !== null && selected.id.id === d.id)
      return index + 1
    }

    switch (e.keyCode) {
      case 38://up
        e.preventDefault()

        if (selectFirstRow()) return

        const prevIndex = selectNextUp()
        if (prevIndex < 0) return
        const prev = sorted[prevIndex]

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {
          //

        } else {
          selectContact(prev)
        }
        break
      case 40://down
        e.preventDefault()

        if (selectFirstRow()) return

        const nextIndex = selectNextDown()
        if (nextIndex >= sorted.length) return
        const next = sorted[nextIndex]

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {
          //
        } else {
          selectContact(next)
        }

        break
    }
  }

  const onKeyDownRef = useRef(onKeyDown)

  useEffect(() => {
    onKeyDownRef.current = onKeyDown
  })

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      onKeyDownRef.current(e)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const onColumnResize = useCallback((newResized: Resize[], event: any) => {
    newResized.forEach(element => {
      if (refColumnResizeState.current) {
        refColumnResizeState.current = { ...refColumnResizeState.current, [element.id]: element.value }
      } else {
        refColumnResizeState.current = { [element.id]: element.value }
      }
    });
  }, [])

  useEffect(() => {
    // need to send this a destruction of queue page to save widths for next time
    return () => {
      if (refColumnResizeState.current) {
        dispatch(actions.requestSetGridColumnWidth(gridType, refColumnResizeState.current))
      }
      refColumnResizeState.current = undefined;
    }

  }, [dispatch])

  if (!myself || !myId || !access) return null

  return <div className='ContactTableBody'>
    <ReactTable className={classNames('ContactTable', className)} ref={reactTable}
      data={data}
      columns={getColumns(onMore, widths)}
      showPagination={true}
      page={paging.page}
      pageSize={paging.pageSize}
      pages={pages}
      minRows={0}
      loading={!contacts}
      NoDataComponent={() => null}
      getTrProps={(_: any, row?: TableRow<ContactBasic>): React.HtmlHTMLAttributes<HTMLTableRowElement> | undefined => row && ({
        className: selected && row.original.id === selected.id.id && row.original.server === selected.id.server ? 'selected' : undefined,
        onContextMenu(e) {
          setMenuState({ target: targetFromEvent(e), item: row.original })
        },
        onClick() {
          if (row.original.business) {
            dispatch(actions.setSelectedNumber(row.original.business))
          } else if (row.original.mobile) {
            dispatch(actions.setSelectedNumber(row.original.mobile))
          } else if (row.original.home) {
            dispatch(actions.setSelectedNumber(row.original.home))
          }

          dispatch(actions.setSelectedContact(row.original))
        },
      })}
      manual
      onFetchData={({ page, pageSize }) => {
        setPaging({ page, pageSize })
      }}
      sorted={sorted}
      onSortedChange={(newSort, column) => {
        setSorted([newSort[0]])
        dispatch(actions.requestSetGridSortOrder(gridType, [newSort[0]]))
      }}
      onResizedChange={onColumnResize}
    />
    {menuState && <ContactMenu state={menuState}
      onClose={() => setMenuState(undefined)}
      onOpen={(item) => {
        dispatch(actions.setSelectedContact(item))
      }}
      onNew={(item) => {
        dispatch(actions.setSelectedContact({ id: '', server: '' }))
      }} />}

    {selected && view.contactType !== undefined && hasAccess(selected.id, view.contactType, access) && renderSelection(selected.id, view.contactType, () => {
      dispatch(actions.setSelectedContact(undefined))
    }, selected.description)}
  </div>
}

function renderSelection(contact: Identifier, contactType: ContactType, onClose: () => void, description?: string) {
  return <div className='ContactPage'>
    <ContactForm contactId={contact.id} contactType={contactType} server={contact.server} description={description} onCancel={() => onClose()} />
  </div>
}

function getColumns(onMore: (e: React.MouseEvent, item: ContactBasic, target: MenuTarget) => void, columnsWidths?: ColumnWidths): Array<TableColumn<ContactBasic>> {

  const columns: Array<TableColumn<ContactBasic>> = [{
    id: 'description',
    Header: 'Details',
    accessor: a => a.description,
    Cell: ({ original: row }) => <div className='info'>
      <ContactInfo contact={row} />
    </div>,
  },
  {
    id: 'business',
    Header: 'Business',
    accessor: a => a.business,
  },
  {
    id: 'mobile',
    Header: 'Mobile',
    accessor: a => a.mobile,
  },
  {
    id: 'home',
    Header: 'Home',
    accessor: a => a.home,
  },
  {
    id: 'email',
    Header: 'Email',
    accessor: a => a.email,
  }, {
    id: 'actions',
    Cell: ({ original: data }) => <div className='actions'>
      <MoreButton className='menu-button' onClick={e => {
        e.stopPropagation()
        onMore(e, data, targetFromEvent(e))
      }
      } />
    </div>,
    width: 40,
    sortable: false,
    resizable: false,
  },
  ]

  return columns.map(x => {
    if (x.id && columnsWidths && columnsWidths[x.id]) {
      return {
        ...x,
        width: columnsWidths[x.id],
      }
    }
    return x
  })
}

export function hasAccess(contact: Identifier, contactType: ContactType, access: Access): boolean | undefined {
  if (contactType === ContactType.None) return false

  if (contact.id === '') {
    if (contactType === ContactType.Personal) {
      return access.features.directoryPersonalContactAdd
    } else {
      return access.features.directoryCompanyContactAdd
    }
  } else {
    if (contactType === ContactType.Personal) {
      return access.features.directoryPersonalContactEdit
    } else {
      return access.features.directoryCompanyContactEdit
    }
  }
}

export function ContactInfo({ contact }: { contact: ContactBasic }) {
  return <>
    <ProfilePhoto className='photo' name={contact.description} contact={contact} />
    <div>{contact.description}</div>
  </>
}

export function ContactMenu({ state: { item, target }, onClose, onOpen, onNew, onClick }: { state: ContactMenuState, onClose(): void, onOpen(item: ContactBasic): void, onNew(item: ContactBasic): void, onClick?(): void }) {
  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end' onClick={onClick}>
    <ContactMenuItems item={item} onOpen={onOpen} onNew={onNew} />
  </Menu>
}

export function ContactMenuItems({ item, onOpen, onNew }: { item: ContactBasic, onOpen(contact: ContactBasic): void, onNew(contact: ContactBasic): void }) {
  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))
  const dispatch = useDispatch()
  const access = useSelector(selectMyAccess)
  const e = parseInt(item.extensionNumber)
  const isFav = useSelector(s => isFavouriteContact(s, item))

  if (!extension || !access || !myId) return null

  function onFav(add: boolean) {
    dispatch(actions.favouriteContact(item, add))
  }

  const answered = extension.status === ExtensionStatus.Answered
  const released = extension.status === ExtensionStatus.None || extension.status === ExtensionStatus.Released

  let edit: boolean | undefined = false
  if (item.extensionNumber === "0" || item.extensionNumber === "-1") {
    edit = access.features.directoryCompanyContactEdit
  } else if (item.extensionNumber === myId.id) {
    edit = access.features.directoryPersonalContactEdit
  } else {
    edit = false
  }
  let add: boolean | undefined = false
  if (item.extensionNumber === "0" || item.extensionNumber === "-1") {
    add = access.features.directoryCompanyContactAdd
  } else if (item.extensionNumber === myId.id) {
    add = access.features.directoryPersonalContactAdd
  } else {
    add = false
  }

  return <>
    <MenuItem disabled={!add}
      onClick={() => {
        onNew(item)
      }} >
      <NewIcon />
      New
      </MenuItem>
    <MenuDivider />
    {renderDialMenuItem('business', <DialIcon />, 'Business', (answered ? access.features.phoneXferBlind : access.features.phoneDial))}
    {renderDialMenuItem('mobile', <DialMobileIcon />, 'Mobile', (answered ? access.features.phoneXferBlind : access.features.phoneDial))}
    {renderDialMenuItem('home', <DialHomeIcon />, 'Home', (answered ? access.features.phoneXferBlind : access.features.phoneDial))}
    <MenuItem disabled={!item.email} onClick={(e) => {
      e.stopPropagation()
      e.preventDefault()
      dispatch(actions.ccMailto(item.email))
    } }>
      <EmailIcon />
      Email
      </MenuItem>
    {(access.features.extensionPSChat || access.features.extensionPSChatExternal) && isBrowser && <MenuItem disabled={!item.email}
      onClick={() => dispatch(actions.requestOneOnOneConversation(item, EntityType.Contact))}>
      <InstantMessagerIcon />
      Send an Instant Message...
      </MenuItem>}
    <MenuDivider />
    <MenuItem disabled={e < 1 && !access.features.directoryCompanyContactDelete || e > 0 && !access.features.directoryPersonalContactDelete}
      onClick={() => dispatch(actions.requestDeleteContact('', item, item.description))} >
      <DeleteIcon />
      Delete
      </MenuItem>
    <MenuDivider />
    {(access.features.companyContactsAccess || access.features.directoryCompanyContactAdd || access.features.directoryPersonalContactAdd) && (!isFav ?
      <MenuItem onClick={() => onFav(true)}>
        <FavIcon />
        Add to favourites
      </MenuItem>
      :
      <MenuItem onClick={() => onFav(false)}>
        <FavRemoveIcon />
        Remove from favourites
      </MenuItem>)}
    <MenuDivider />
    <MenuItem disabled={!edit} onClick={() => onOpen(item)}>
      <ExtesnionPropsIcon />
      Properties
      </MenuItem>
  </>

  function renderDialMenuItem(key1: keyof ContactBasic, icon: React.ReactNode, title: string, access: Boolean | undefined) {
    if (!item) { return }
    const number = item[key1]

    const handleClick =
      answered ? (number: string) => dispatch(actions.ccBlindTransfer(number)) :
        (number: string) => dispatch(actions.ccDial(number))

    const prefix =
      answered ? 'Transfer to ' :
        'Dial '

    const disabled =
      answered ? !answered :
        !released

    return access && <MenuItem key={title} disabled={!number || disabled} onClick={() => handleClick(number!)}>
      {icon}
      {prefix + title}
    </MenuItem>
  }

}


